import React, { useCallback, useRef, useState } from 'react'
import Papa from 'papaparse'
import { faLongArrowRight } from '@fortawesome/free-solid-svg-icons'
import { Content, Data, Form, Navigation, Input } from 'ui'
import { toast } from 'react-hot-toast'
import { concatStyles, wait } from 'utils'
import {
  DataManagementShell,
  DataManagementShellProps,
} from './DataManagementShell'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import {
  useCreateUploadsMutation,
  UploadBucket,
  useProcessUploadCsvMutation,
  ProcessJobType,
} from 'epc-schema'

type MappedResultValue = { [key: string]: string }

export type BulkDataConfirmHandler = (fileKey: string) => Promise<void> | void

interface MainContentProps {
  previews?: Form.FilePreview[]
  fields: MappedResultValue
  onSetPreviews?: (previews: Form.FilePreview[]) => void
  tableHeaders?: string[]
  tableData: string[][]
  ignoreHeaders?: boolean
  wipeContent?: boolean
  onIgnoreHeader?: () => void
  onWipeContent?: () => void
  maxPreviews?: number
  jobType: ProcessJobType
  selectedFields: FieldMap
  onSelectedField: (name: string, idx: number) => void
  onCancel?: () => void
  onConfirm: BulkDataConfirmHandler
}

const DROPDOWN_WRAPPER_STYLES: string = 'flex flex-col flex flex-wrap my-12'
const ROW_STYLES: string = 'flex flex-row flex justify-between w-full mb-1'
const DROPDOWN_STYLES: string = 'w-5/12'
const ICON_STYLES: string =
  'flex flex-row flex justify-center items-center w-1/12'
const TEXT_STYLES: string = 'flex flex-row flex items-center w-4/12'

const MainContent: React.FC<MainContentProps> = ({
  previews = [],
  onSetPreviews,
  tableHeaders,
  tableData,
  fields,
  ignoreHeaders,
  wipeContent,
  onIgnoreHeader: handleOnIgnoreHeader,
  onWipeContent: handleWipeContent,
  maxPreviews = 4,
  selectedFields,
  onSelectedField: handleOnSelectedField,
  onCancel: handleOnCancel,
  onConfirm: handleOnConfirm,
  jobType,
}) => {
  const [processUpload] = useProcessUploadCsvMutation()
  const [fileKey, setFileKey] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const handleOnUpdate: Form.HandleAssociatedFileUpdateFn = useCallback(
    (files) => {
      onSetPreviews?.(
        files.map((file) => ({
          ...file,
          name: file.file?.name ?? file?.name ?? file.id,
        }))
      )
    },
    [onSetPreviews]
  )

  const handleOnUpload: Form.HandleFileUploadFn = useCallback((file) => {
    console.log('upload ', file)
  }, [])

  const [makeUpload] = useCreateUploadsMutation()

  const handleGetEndpointsForFilenames: Form.FileToEndpointFn = async (
    files
  ) => {
    const result = await makeUpload({
      variables: {
        uploadInput: {
          objects: files.map(({ name, contentType }) => ({
            key: name,
            contentType,
          })),
          bucket: UploadBucket.Pending,
        },
      },
    })
    const endpoints = (result.data?.response?.uploads ?? []).map((upload) => ({
      uploadUrl: upload?.url ?? '',
      downloadUrl: upload?.downloadUrl ?? '',
      id: upload?.id ?? '',
    }))
    setFileKey(endpoints[0]?.id)
    return endpoints
  }

  const handleFileError: Form.HandleFileUploadErrorFn = (e: Error) => {
    toast.error(e.message)
  }

  const handleImport = async () => {
    const results = [...tableData]
    if (!ignoreHeaders) {
      results.shift()
    }
    setLoading(true)
    if (fileKey) {
      try {
        const result = await processUpload({
          variables: {
            mappings: Object.keys(selectedFields).map((name) => ({
              name,
              index: selectedFields[name],
            })),
            wipeExistingData: wipeContent,
            includeHeaderRow: ignoreHeaders,
            fileKey,
            jobType,
          },
        })
        if (result.data?.response.success) {
          toast.success('Your data is now being processed.', {
            duration: 5000,
          })
          await wait(1)
          toast.success(
            'Please allow up to 10 minutes before your changes take affect.',
            { duration: 30000 }
          )
          await handleOnConfirm(fileKey)
        } else {
          result.data?.response.errors?.map((error) =>
            toast.error(`${error?.path}: ${error?.message}`)
          )
        }
      } catch (e) {
        toast.error(e.message)
        toast.error(`There was a problem importing schools.`)
      }
    } else {
      toast.error('No file was specified for processing.')
    }
    setLoading(false)
  }

  const totalRows = tableData?.length ?? 0
  const cols = tableHeaders?.length ?? 0
  const colWidth = `${100.0 / cols}%`
  const selectedValues = Object.values(selectedFields)
  const uniqueValues = selectedValues.filter(
    (v, idx) => selectedValues.indexOf(v) === idx
  )
  const incomplete = uniqueValues.length < Object.keys(fields).length

  return (
    <>
      <Content.SubHeading>Step 1: Choose CSV data file</Content.SubHeading>
      <Form.AttachmentList
        accept={['.csv']}
        previews={previews}
        maxFiles={1}
        onError={handleFileError}
        onUpdate={handleOnUpdate}
        onUpload={handleOnUpload}
        getEndpointsForFilenames={handleGetEndpointsForFilenames}
      />
      <Content.SubHeading>Step 2: Map data fields</Content.SubHeading>
      <Content.Body>
        Tell us where to place your data. The table below shows the first few
        rows of the file you just uploaded.
      </Content.Body>
      {totalRows > 0 ? (
        <div className="overflow-x-scroll">
          <Data.Table>
            <thead>
              <Data.Row>
                {tableHeaders?.map((header: string, idx: number) => (
                  <Data.Header
                    className="!text-secondary"
                    width={colWidth}
                    key={`${header}${idx}`}
                  >
                    {header}
                  </Data.Header>
                ))}
              </Data.Row>
            </thead>
            <tbody>
              {tableData?.map((values: string[], row: number) =>
                row > 0 && row < maxPreviews ? (
                  <Data.Row key={`dataPreviewRow${row}`}>
                    {values.map((value, idx) =>
                      idx < cols ? (
                        <Data.Cell
                          className="truncate"
                          width={colWidth}
                          key={`${value}${row}${idx}`}
                        >
                          {value as string}
                        </Data.Cell>
                      ) : null
                    )}
                  </Data.Row>
                ) : null
              )}
              {maxPreviews < totalRows ? (
                <Data.Row>
                  <Data.Cell className="font-bold text-primary">
                    + {totalRows - maxPreviews} more rows...
                  </Data.Cell>
                </Data.Row>
              ) : null}
            </tbody>
          </Data.Table>
        </div>
      ) : null}
      <div
        className={concatStyles(
          DROPDOWN_WRAPPER_STYLES,
          totalRows < 1 && 'pointer-events-none opacity-50'
        )}
      >
        {Object.keys(fields).map((field) => {
          const selectedFieldIdx = selectedFields[field] ?? -1
          const isUnique =
            selectedFieldIdx < 0 ||
            selectedValues.filter((v) => v === selectedFieldIdx).length <= 1

          return (
            <div className={ROW_STYLES} key={field}>
              <div className={DROPDOWN_STYLES}>
                <Form.DropdownField
                  placeholder="CSV Column"
                  value={tableHeaders?.[selectedFieldIdx]}
                  name={fields[field]}
                  error={!isUnique}
                  errorMessage={!isUnique ? 'Must be unique.' : ''}
                  pushContent
                >
                  {(searchValue: string) =>
                    tableHeaders
                      ?.filter(
                        (s) =>
                          searchValue.length < 1 ||
                          s.toLowerCase().indexOf(searchValue.toLowerCase()) >
                            -1
                      )
                      .map((val, idx) => (
                        <Form.DropdownItem
                          onClick={() => handleOnSelectedField?.(field, idx)}
                          selected={idx === selectedFieldIdx}
                          key={val}
                        >
                          {val}
                        </Form.DropdownItem>
                      )) ?? []
                  }
                </Form.DropdownField>
              </div>
              <div className={ICON_STYLES}>
                <Navigation.IconButton
                  icon={faLongArrowRight}
                  appearance="actionable"
                />
              </div>
              <div className={TEXT_STYLES}>
                <Content.Body className="xl:text-base">
                  Database: {fields[field]}
                </Content.Body>
              </div>
            </div>
          )
        })}
        <div className="flex flex-row flex items-center">
          <div className="w-8 h-8">
            <Input.CheckBox
              name="header-row"
              checked={ignoreHeaders}
              value={JSON.stringify(ignoreHeaders)}
              onChange={handleOnIgnoreHeader}
            />
          </div>
          <div className="text-gray-600">
            <Content.Body className="xl:text-base">
              Include header row in import (if first row of your data is not the
              field names)
            </Content.Body>
          </div>
        </div>
        <div className="flex flex-row flex items-center">
          <div className="w-8 h-8">
            <Input.CheckBox
              name="header-row"
              checked={wipeContent}
              value={JSON.stringify(wipeContent)}
              onChange={handleWipeContent}
            />
          </div>
          <div className="text-gray-600">
            <Content.Body className="xl:text-base">
              Wipe existing content prior to import
            </Content.Body>
          </div>
        </div>
        <div className="flex flex-row flex justify-end items-center">
          {handleOnCancel ? (
            <div className="w-48 mx-1">
              <Content.ActionButton
                onClick={handleOnCancel}
                label="Cancel"
                icon={faTimes}
                appearance="destructive"
                disabled={loading}
              />
            </div>
          ) : null}
          <div className="w-48 mx-1">
            <Content.ActionButton
              onClick={handleImport}
              label="Import Data"
              loading={loading}
              disabled={incomplete}
            />
          </div>
        </div>
      </div>
    </>
  )
}

type FieldMap = Record<string, number>

interface BulkDataViewProps extends DataManagementShellProps {
  fields: MappedResultValue
  onCancel?: () => void
  onConfirm: BulkDataConfirmHandler
  jobType: ProcessJobType
}

export const BulkDataView: React.FC<BulkDataViewProps> = ({
  onCancel: handleOnCancel,
  onConfirm: handleOnConfirm,
  jobType,
  ...props
}) => {
  const actualTableHeaders: React.MutableRefObject<string[] | []> = useRef<
    string[]
  >([])
  const [selectedFields, setSelectedFields] = useState<FieldMap>({})
  const [previews, setPreviews] = useState<Form.FilePreview[] | undefined>()
  const [tableData, setTableData] = useState<unknown[]>()
  const [tableHeaders, setTableHeaders] = useState<string[]>([])
  const [ignoreHeader, setIgnoreHeader] = useState<boolean>(false)
  const [wipeContent, setWipeContent] = useState<boolean>(false)

  const handleOnIgnoreHeader = useCallback(
    () => setIgnoreHeader((prev) => !prev),
    []
  )

  const handleWipeContent = useCallback(
    () => setWipeContent((prev) => !prev),
    []
  )

  const complete = ({ data }: Papa.ParseResult<unknown>) => {
    const tableHeaders: string[] = (data[0] as string[]).map((s, i) =>
      s.length > 0 ? s : `Column ${i}`
    )
    setTableData(data)
    actualTableHeaders.current = tableHeaders
    setTableHeaders(tableHeaders)
  }

  const error = (error: Papa.ParseError) => toast.error(error.message)

  const handleOnSetPreviews = useCallback((previews = []) => {
    if (previews.length > 0) {
      const [{ file }] = previews
      Papa.parse(file, { complete, error })
    }
    setPreviews(previews)
    setTableData([])
  }, [])

  const handleOnSelectedField = useCallback(
    (name: string, idx: number) =>
      setSelectedFields((prevSelectedFields) => ({
        ...prevSelectedFields,
        [name]: idx,
      })),
    []
  )

  return (
    <DataManagementShell
      returnUrl={props.returnUrl}
      linkText={props.linkText}
      heading={props.heading}
      main={
        <MainContent
          previews={previews}
          onSetPreviews={handleOnSetPreviews}
          tableHeaders={tableHeaders}
          tableData={(tableData ?? []) as string[][]}
          ignoreHeaders={ignoreHeader}
          onIgnoreHeader={handleOnIgnoreHeader}
          wipeContent={wipeContent}
          onWipeContent={handleWipeContent}
          fields={props.fields}
          selectedFields={selectedFields}
          onCancel={handleOnCancel}
          onConfirm={handleOnConfirm}
          onSelectedField={handleOnSelectedField}
          jobType={jobType}
        />
      }
      aside={props.aside}
    />
  )
}
